import { ApiPaths, api } from "@/lib/api";

import type {
  DsrExportJobStatus,
  DsrParticipantEraseSummary,
  DsrParticipantExport,
  DsrUserEraseSummary,
  DsrUserExport,
  OrgMember,
  SessionParticipant,
} from "./types";


export async function fetchOrgMembers(
  status?: "active" | "inactive" | "all",
): Promise<OrgMember[]> {
  const res = await api.get<OrgMember[]>(
    ApiPaths.orgMembers,
    status ? { params: { status } } : undefined,
  );
  return res.data;
}


export async function exportUser(userId: number): Promise<DsrUserExport> {
  const res = await api.post<DsrUserExport>(ApiPaths.dsrUserExport(userId));
  return res.data;
}


export async function eraseUser(userId: number): Promise<DsrUserEraseSummary> {
  const res = await api.post<DsrUserEraseSummary>(ApiPaths.dsrUserErase(userId));
  return res.data;
}


export async function startExportArchive(
  userId: number,
): Promise<{ job_id: string }> {
  const res = await api.post<{ job_id: string }>(
    ApiPaths.dsrUserExportArchive(userId),
  );
  return res.data;
}


export async function fetchExportStatus(
  jobId: string,
): Promise<DsrExportJobStatus> {
  const res = await api.get<DsrExportJobStatus>(ApiPaths.dsrExportStatus(jobId));
  return res.data;
}


/** Trigger a JSON download of the export bundle in the user's browser. */
export function downloadExportBundle(args: {
  bundle: DsrUserExport;
  filename?: string;
}) {
  const filename =
    args.filename ?? `dsr-export-user-${args.bundle.subject_id}.json`;
  const blob = new Blob([JSON.stringify(args.bundle, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}


// ---------- Phase 2B-dsr-3-ui — session participants ----------


export async function fetchSessionParticipants(
  sessionId: string,
  opts: { includePending?: boolean } = {},
): Promise<SessionParticipant[]> {
  const url = opts.includePending
    ? `${ApiPaths.sessionParticipants(sessionId)}?include_pending=true`
    : ApiPaths.sessionParticipants(sessionId);
  const res = await api.get<SessionParticipant[]>(url);
  return res.data;
}


export async function exportParticipant(
  participantId: string,
): Promise<DsrParticipantExport> {
  const res = await api.post<DsrParticipantExport>(
    ApiPaths.dsrParticipantExport(participantId),
  );
  return res.data;
}


export async function eraseParticipant(
  participantId: string,
): Promise<DsrParticipantEraseSummary> {
  const res = await api.post<DsrParticipantEraseSummary>(
    ApiPaths.dsrParticipantErase(participantId),
  );
  return res.data;
}


/** JSON-download helper for participant bundles. Mirrors the user
 *  flow but with a participant-specific filename. */
export function downloadParticipantExportBundle(args: {
  bundle: DsrParticipantExport;
  filename?: string;
}) {
  const filename =
    args.filename ??
    `dsr-export-participant-${args.bundle.subject_id}.json`;
  const blob = new Blob([JSON.stringify(args.bundle, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
